import { useState, useEffect } from 'react';
import { Box, Skeleton, Dialog, DialogContent, IconButton } from '@mui/material';
import { BrokenImage as BrokenImageIcon, Close as CloseIcon, ZoomIn as ZoomInIcon } from '@mui/icons-material';
import api from '../../services/api';

interface DrugImageThumbnailProps {
  din: string;
  size?: number;
  borderRadius?: number;
  showPlaceholder?: boolean;
  /** Allow clicking the image to expand it in a lightbox dialog */
  expandable?: boolean;
}

/**
 * Displays a drug image thumbnail from the drug_pictures table.
 * Fetches via authenticated API and converts to blob URL.
 * Hides gracefully if no image is available.
 */
export default function DrugImageThumbnail({
  din,
  size = 64,
  borderRadius = 4,
  showPlaceholder = false,
  expandable = false,
}: DrugImageThumbnailProps) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!din) {
      setLoading(false);
      setError(true);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(false);
    setBlobUrl(null);

    api
      .get(`/api/drug-pictures/din/${din}/image`, { responseType: 'blob' })
      .then((res) => {
        if (cancelled) return;
        const url = URL.createObjectURL(res.data);
        setBlobUrl(url);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setError(true);
        setLoading(false);
      });

    return () => {
      cancelled = true;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [din]);

  // Cleanup blob URL on unmount
  useEffect(() => {
    return () => {
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [blobUrl]);

  if (!din) return null;

  if (error) {
    if (!showPlaceholder) return null;
    return (
      <Box
        sx={{
          width: size,
          height: size,
          borderRadius: `${borderRadius}px`,
          bgcolor: 'grey.100',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: '1px solid',
          borderColor: 'grey.300',
          flexShrink: 0,
        }}
      >
        <BrokenImageIcon sx={{ fontSize: size * 0.4, color: 'grey.400' }} />
      </Box>
    );
  }

  if (loading) {
    return (
      <Skeleton
        variant="rounded"
        width={size}
        height={size}
        sx={{ borderRadius: `${borderRadius}px`, flexShrink: 0 }}
      />
    );
  }

  return (
    <>
      <Box
        sx={{
          position: 'relative',
          width: size,
          height: size,
          flexShrink: 0,
          cursor: expandable ? 'pointer' : 'default',
          '&:hover .zoom-icon': expandable ? { opacity: 1 } : {},
        }}
        onClick={expandable ? (e) => { e.stopPropagation(); setExpanded(true); } : undefined}
      >
        <Box
          component="img"
          src={blobUrl!}
          alt={`Drug ${din}`}
          sx={{
            width: size,
            height: size,
            objectFit: 'contain',
            borderRadius: `${borderRadius}px`,
            border: '1px solid',
            borderColor: 'grey.200',
            bgcolor: 'white',
          }}
        />
        {expandable && (
          <Box
            className="zoom-icon"
            sx={{
              position: 'absolute',
              bottom: 2,
              right: 2,
              bgcolor: 'rgba(0,0,0,0.5)',
              borderRadius: '50%',
              width: 22,
              height: 22,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: 0,
              transition: 'opacity 0.2s',
            }}
          >
            <ZoomInIcon sx={{ fontSize: 14, color: 'white' }} />
          </Box>
        )}
      </Box>

      {expandable && expanded && (
        <Dialog
          open={expanded}
          onClose={() => setExpanded(false)}
          maxWidth={false}
          slotProps={{
            paper: {
              sx: {
                bgcolor: 'transparent',
                boxShadow: 'none',
                overflow: 'visible',
              },
            },
          }}
        >
          <DialogContent
            sx={{
              p: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
              bgcolor: 'white',
              borderRadius: 2,
              overflow: 'hidden',
            }}
          >
            <IconButton
              onClick={() => setExpanded(false)}
              sx={{
                position: 'absolute',
                top: 4,
                right: 4,
                bgcolor: 'rgba(0,0,0,0.4)',
                color: 'white',
                zIndex: 1,
                '&:hover': { bgcolor: 'rgba(0,0,0,0.6)' },
              }}
              size="small"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
            <Box
              component="img"
              src={blobUrl!}
              alt={`Drug ${din}`}
              sx={{
                maxWidth: '80vw',
                maxHeight: '80vh',
                objectFit: 'contain',
                display: 'block',
              }}
            />
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
